import * as z from 'zod/v4';

import { type Country, countryIdSchema } from './Country.js';

/**
 * Zod schema for Participant play entity
 */
export const participantPlayTypeEnum = z.enum(['Team', 'Player']).default('Team');

/**
 * TS Participant play type
 */
export type ParticipantPlayType = z.infer<typeof participantPlayTypeEnum>;

/**
 * Zod schema for Participant entity
 */
export const participantTypeEnum = z
  .string()
  .toLowerCase()
  .pipe(z.enum(['home', 'away']));

export const participantIdSchema = z.number().int().nonnegative().brand<'ParticipantId'>();

export type ParticipantId = z.infer<typeof participantIdSchema>;

/**
 * TS Participant type
 */
export type ParticipantType = z.infer<typeof participantTypeEnum>;

const participantBaseSchema = z.object({
  id: participantIdSchema,
  name: z.string(),
  shortName: z.string(),
  mediumName: z.string(),
  arenaId: z.number().optional(),
  managerId: z.number().optional(),
  trend: z.number().optional(),
  isoCode: z.string().optional(),
  type: participantTypeEnum,
});

/**
 * TS Participant type
 */
export type Participant = z.infer<typeof participantSchema>;

/**
 * Zod schema for Participant entity
 */
export const participantSchema = z.object({
  ...participantBaseSchema.shape,
  countryId: countryIdSchema,
});

/**
 * Composite type for Participant entity
 * Used to parse the response from the API into a Participant entity
 */
export const participantTransformSchema = z
  .object({
    ...participantBaseSchema.shape,
    type: participantTypeEnum,
    // country data
    countryId: countryIdSchema,
    countryName: z.string(),
    isoCode: z.string(),
  })
  .transform(({ countryId, countryName, isoCode, type, ...restParticipant }) => {
    const country: Country = {
      countryId,
      name: countryName,
      isoCode,
    };

    const participant: Participant = {
      ...restParticipant,
      countryId,
      isoCode,
      type,
    };

    return {
      participant,
      type,
      country,
    };
  });
